// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { costSchema } from '@/costs/api/types';
import { Currency } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';

const createCostResponseSchema = z
  .object({
    data: costSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, cost: data }));

type CreateCostResponse = z.infer<typeof createCostResponseSchema>;

interface CreateCostParams {
  amount?: Currency | null;
  description?: string;
  costIncurredOn: DateTime;
  category?: string;
  projectId?: string | null;
  personId?: string | null;
}

/**
 * Creates a new cost using the direct cost endpoint.
 *
 * @param params - The cost creation parameters
 */
export async function createCost(
  params: CreateCostParams
): Promise<CreateCostResponse> {
  const url = '/v1/cost';
  const response = await api.post(url, {
    amount: serializeOptionalCurrencyForAPI(params.amount),
    description: params.description,
    costIncurredOn: serializeDateForAPI(params.costIncurredOn),
    category: params.category,
    projectId: params.projectId,
    personId: params.personId,
  });

  return parseResponse(createCostResponseSchema, response.data, {
    schemaName: 'CreateCost',
    endpoint: url,
  });
}
